/**
 * Dates and times, Australian.
 *
 * Every date in a Beam report is written `13 May 2024`. That is stated in the
 * repository rules and shown in the IME base template, which spells the
 * placeholder as `day month year` and adds `use this format for all dates`.
 *
 * The hard part is not the output. It is deciding what `05/06/2024` means. In
 * Australia it is 5 June. The same string typed by a United States insurer, or
 * produced by a model trained mostly on United States text, is 6 May. Guessing
 * wrong puts a date of injury out by a month in a legal document, so the rule
 * here is: day-first when the numbers permit it, and where the string is
 * genuinely ambiguous the change is marked `likely` so a human sees it.
 */
export const MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];
const MONTH_LOOKUP = new Map();
MONTHS.forEach((m, i) => {
    MONTH_LOOKUP.set(m.toLowerCase(), i + 1);
    MONTH_LOOKUP.set(m.slice(0, 3).toLowerCase(), i + 1);
});
MONTH_LOOKUP.set('sept', 9);
const ORDINAL_WORDS = {
    first: 1, second: 2, third: 3, fourth: 4, fifth: 5, sixth: 6, seventh: 7,
    eighth: 8, ninth: 9, tenth: 10, eleventh: 11, twelfth: 12, thirteenth: 13,
    fourteenth: 14, fifteenth: 15, sixteenth: 16, seventeenth: 17,
    eighteenth: 18, nineteenth: 19, twentieth: 20, thirtieth: 30,
    'twenty first': 21, 'twenty second': 22, 'twenty third': 23,
    'twenty fourth': 24, 'twenty fifth': 25, 'twenty sixth': 26,
    'twenty seventh': 27, 'twenty eighth': 28, 'twenty ninth': 29,
    'thirty first': 31,
};
/** Render a day, month and year the Australian way. */
export function formatAuDate(day, month, year) {
    const name = MONTHS[month - 1];
    if (!name)
        throw new RangeError(`month out of range: ${month}`);
    return day ? `${day} ${name} ${year}` : `${name} ${year}`;
}
function normaliseYear(raw) {
    const n = Number(raw);
    if (raw.length === 4)
        return n;
    // Two digit years in a medicolegal file are almost always recent history.
    return n <= 40 ? 2000 + n : 1900 + n;
}
function valid(day, month, year) {
    if (month < 1 || month > 12)
        return false;
    if (day < 1)
        return false;
    const d = new Date(Date.UTC(year, month - 1, day));
    return d.getUTCMonth() === month - 1 && d.getUTCDate() === day;
}
/**
 * `May 13, 2024` and `May 13 2024`, the United States order, rewritten to
 * `13 May 2024`. Unambiguous, so always `certain`.
 */
const US_LONG = /\b([A-Za-z]{3,9})\.?\s+(\d{1,2})(?:st|nd|rd|th)?\s*,?\s+(\d{4})\b/g;
/** `13 May 2024`, `13th May 2024`, `13 May, 2024`. Already Australian order. */
const AU_LONG = /\b(\d{1,2})(?:st|nd|rd|th)?\s+([A-Za-z]{3,9})\.?\s*,?\s+(\d{2,4})\b/g;
/** `May 2024`, a month and year with no day. */
const MONTH_YEAR = /\b([A-Za-z]{3,9})\.?\s+(\d{4})\b/g;
/** `13/05/2024`, `13-05-2024`, `13.05.24`. */
const NUMERIC = /\b(\d{1,2})([/\-.])(\d{1,2})\2(\d{2,4})\b/g;
/** `2024-05-13`, ISO. Unambiguous. */
const ISO = /\b(\d{4})-(\d{2})-(\d{2})\b/g;
/** `the thirteenth of May twenty twenty four` and friends, from dictation. */
const SPOKEN = new RegExp(String.raw `\b(?:the\s+)?(` +
    Object.keys(ORDINAL_WORDS).sort((a, b) => b.length - a.length).join('|') +
    String.raw `)\s+of\s+([A-Za-z]{3,9})(?:\s+(?:(\d{4})|(twenty\s+twenty(?:\s+[a-z]+)?|two\s+thousand(?:\s+and)?(?:\s+[a-z]+){0,2})))?\b`, 'gi');
function spokenYear(phrase) {
    const words = phrase.toLowerCase().replace(/\s+and\s+/g, ' ').trim().split(/\s+/);
    const unit = {
        zero: 0, one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7,
        eight: 8, nine: 9, ten: 10, eleven: 11, twelve: 12, thirteen: 13,
        fourteen: 14, fifteen: 15, sixteen: 16, seventeen: 17, eighteen: 18,
        nineteen: 19, twenty: 20, thirty: 30,
    };
    if (words[0] === 'twenty' && words[1] === 'twenty') {
        const rest = words.slice(2);
        if (rest.length === 0)
            return 2020;
        const v = rest.reduce((acc, w) => acc + (unit[w] ?? 0), 0);
        return 2020 + v;
    }
    if (words[0] === 'two' && words[1] === 'thousand') {
        const rest = words.slice(2);
        if (rest.length === 0)
            return 2000;
        const v = rest.reduce((acc, w) => acc + (unit[w] ?? 0), 0);
        return 2000 + v;
    }
    return null;
}
/**
 * Find every date in the text and say what it should be.
 *
 * Hits are returned in document order and may not overlap; the caller applies
 * them as edits so that the number and measure passes cannot reach inside a date.
 */
export function findDates(text) {
    const hits = [];
    const claimed = [];
    const free = (s, e) => !claimed.some(([cs, ce]) => s < ce && cs < e);
    const claim = (s, e) => { claimed.push([s, e]); };
    const push = (s, e, replacement, confidence, note) => {
        if (!free(s, e))
            return;
        claim(s, e);
        // Identical spans are still emitted. They claim the range in the shared
        // edit pool so that the number and measure passes cannot reach inside a
        // date, and the caller drops them from the change list.
        hits.push({ start: s, end: e, replacement, confidence, ...(note ? { note } : {}) });
    };
    for (const m of text.matchAll(ISO)) {
        const [, y, mo, d] = m;
        const year = Number(y), month = Number(mo), day = Number(d);
        if (!valid(day, month, year))
            continue;
        push(m.index, m.index + m[0].length, formatAuDate(day, month, year), 'certain');
    }
    for (const m of text.matchAll(US_LONG)) {
        const month = MONTH_LOOKUP.get(m[1].toLowerCase());
        if (!month)
            continue;
        const day = Number(m[2]);
        const year = Number(m[3]);
        if (!valid(day, month, year))
            continue;
        push(m.index, m.index + m[0].length, formatAuDate(day, month, year), 'certain', 'United States month-first order rewritten to Australian day-first');
    }
    for (const m of text.matchAll(AU_LONG)) {
        const month = MONTH_LOOKUP.get(m[2].toLowerCase());
        if (!month)
            continue;
        const day = Number(m[1]);
        const year = normaliseYear(m[3]);
        if (!valid(day, month, year))
            continue;
        push(m.index, m.index + m[0].length, formatAuDate(day, month, year), 'certain');
    }
    for (const m of text.matchAll(SPOKEN)) {
        const day = ORDINAL_WORDS[m[1].toLowerCase().replace(/\s+/g, ' ')];
        const month = MONTH_LOOKUP.get(m[2].toLowerCase());
        if (!day || !month)
            continue;
        const year = m[3] ? Number(m[3]) : m[4] ? spokenYear(m[4]) : null;
        if (year === null) {
            push(m.index, m.index + m[0].length, `${day} ${MONTHS[month - 1]}`, 'likely', 'dictated date with no year stated');
            continue;
        }
        if (!valid(day, month, year))
            continue;
        push(m.index, m.index + m[0].length, formatAuDate(day, month, year), 'certain', 'dictated date');
    }
    for (const m of text.matchAll(NUMERIC)) {
        const a = Number(m[1]);
        const b = Number(m[3]);
        const year = normaliseYear(m[4]);
        // Day first is the Australian reading. Fall back to month first only when
        // the first component cannot be a month and the second can.
        if (valid(a, b, year)) {
            const ambiguous = a <= 12 && b <= 12 && a !== b;
            push(m.index, m.index + m[0].length, formatAuDate(a, b, year), ambiguous ? 'likely' : 'certain', ambiguous
                ? `read day-first as Australian convention. ${a}/${b} could also mean ${MONTHS[a - 1]} ${b}. Confirm against the source document.`
                : undefined);
        }
        else if (valid(b, a, year)) {
            push(m.index, m.index + m[0].length, formatAuDate(b, a, year), 'certain', 'month-first source, day exceeds 12 so the order is unambiguous');
        }
    }
    for (const m of text.matchAll(MONTH_YEAR)) {
        const month = MONTH_LOOKUP.get(m[1].toLowerCase());
        if (!month)
            continue;
        const year = Number(m[2]);
        if (year < 1900 || year > 2200)
            continue;
        push(m.index, m.index + m[0].length, formatAuDate(null, month, year), 'certain');
    }
    return hits.sort((x, y) => x.start - y.start);
}
/**
 * Times. Australian style writes a full stop between hours and minutes and
 * closes up the meridiem: `9.30am`, `4.15pm`, `12 noon`.
 */
const TIME = /\b(\d{1,2})[:.](\d{2})\s*([ap])\.?\s?m\.?\b/gi;
const BARE_MERIDIEM = /\b(\d{1,2})\s*([ap])\.?\s?m\.?\b/gi;
export function findTimes(text) {
    const hits = [];
    const claimed = [];
    const free = (s, e) => !claimed.some(([cs, ce]) => s < ce && cs < e);
    for (const m of text.matchAll(TIME)) {
        const hour = Number(m[1]);
        const minute = m[2];
        if (hour < 1 || hour > 12)
            continue;
        const suffix = `${m[3].toLowerCase()}m`;
        const replacement = `${hour}.${minute}${suffix}`;
        claimed.push([m.index, m.index + m[0].length]);
        hits.push({ start: m.index, end: m.index + m[0].length, replacement, confidence: 'certain' });
    }
    for (const m of text.matchAll(BARE_MERIDIEM)) {
        const s = m.index, e = s + m[0].length;
        if (!free(s, e))
            continue;
        const hour = Number(m[1]);
        if (hour < 1 || hour > 12)
            continue;
        const replacement = `${hour}${m[2].toLowerCase()}m`;
        claimed.push([s, e]);
        hits.push({ start: s, end: e, replacement, confidence: 'certain' });
    }
    return hits.sort((x, y) => x.start - y.start);
}
//# sourceMappingURL=dates.js.map